import { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { fmtError } from "@/lib/api";
import { DataTable, Modal, Btn, Field, TextInput, Select, Icons, Badge } from "@/components/ui-lite";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const empty = { email: "", name: "", password: "", role: "viewer", ip_whitelist: [] };

export default function AdminUsersPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [newIp, setNewIp] = useState("");

  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) {
        const update = {
          name: form.name || undefined,
          role: form.role || undefined,
          ip_whitelist: form.ip_whitelist,
        };
        if (form.password) update.password = form.password;
        await api.put(`/users/${editing.id}`, update);
      } else {
        await api.post("/users", form);
      }
      toast.success("Saved"); setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const del = async (u) => {
    if (u.is_super_admin) return toast.error("Cannot delete super admin");
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); }
    catch (e) { toast.error(fmtError(e)); }
  };

  const reset2fa = async (u) => {
    if (!window.confirm(`Reset 2FA for ${u.email}?`)) return;
    try { await api.put(`/users/${u.id}`, { reset_2fa: true }); toast.success("2FA reset — user will enroll on next login"); load(); }
    catch (e) { toast.error(fmtError(e)); }
  };

  const addIp = () => { if (newIp) { setForm({ ...form, ip_whitelist: [...form.ip_whitelist, newIp.trim()] }); setNewIp(""); } };
  const rmIp = (ip) => setForm({ ...form, ip_whitelist: form.ip_whitelist.filter((x) => x !== ip) });

  return (
    <AppShell
      title="Users & Access"
      subtitle="Admin can manage users, roles and per-user IP whitelists (super-admin always bypasses)"
      actions={<Btn data-testid="user-add" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}><Icons.Plus size={16} className="inline mr-1"/> New user</Btn>}
    >
      <DataTable
        testid="users-table"
        columns={[
          { key: "email", label: "Email", mono: true, render: (r) => <span className="font-mono">{r.email}{r.is_super_admin ? <Badge tone="success" >super</Badge> : null}</span> },
          { key: "name", label: "Name" },
          { key: "role", label: "Role", render: (r) => <Badge tone={r.role === "admin" ? "success" : "info"}>{r.role}</Badge> },
          { key: "totp_enabled", label: "2FA", render: (r) => r.totp_enabled ? <Badge tone="success">enabled</Badge> : <Badge tone="warning">pending</Badge> },
          { key: "ip_whitelist", label: "IP whitelist", render: (r) => r.ip_whitelist?.length ? <span className="font-mono text-xs">{r.ip_whitelist.join(", ")}</span> : <span className="text-[var(--wv-text-3)]">— any —</span> },
        ]}
        rows={rows}
        actions={(r) => (
          <div className="flex gap-2">
            <button onClick={() => { setEditing(r); setForm({ email: r.email, name: r.name || "", password: "", role: r.role, ip_whitelist: r.ip_whitelist || [] }); setOpen(true); }} className="text-[var(--wv-text-2)] hover:text-[var(--wv-primary)]"><Icons.Pencil size={16}/></button>
            <button onClick={() => reset2fa(r)} className="text-xs text-[var(--wv-text-2)] hover:text-[var(--wv-primary)]">reset 2FA</button>
            <button onClick={() => del(r)} className="text-[var(--wv-text-2)] hover:text-[var(--wv-error)]"><Icons.Trash2 size={16}/></button>
          </div>
        )}
      />

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Edit ${editing.email}` : "New user"}
        footer={<>
          <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn data-testid="user-save" onClick={save}>Save</Btn>
        </>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Email"><TextInput data-testid="user-email" type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Name"><TextInput data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Role">
            <Select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="viewer">Viewer</option>
              <option value="admin">Admin</option>
            </Select>
          </Field>
          <Field label={editing ? "New password (blank = keep)" : "Password"}>
            <TextInput data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
          <div className="col-span-2">
            <Field label="IP whitelist">
              <div className="flex gap-2">
                <TextInput placeholder="203.0.113.10" value={newIp} onChange={(e) => setNewIp(e.target.value)} className="font-mono"/>
                <Btn variant="secondary" onClick={addIp}><Plus size={14} className="inline mr-1"/> Add</Btn>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {form.ip_whitelist.map((ip) => (
                  <span key={ip} className="inline-flex items-center gap-1 px-2 py-1 bg-white/80 border border-white/70 rounded-full font-mono text-xs">
                    {ip}
                    <button onClick={() => rmIp(ip)} className="text-[var(--wv-text-2)] hover:text-[var(--wv-error)]"><X size={12}/></button>
                  </span>
                ))}
                {form.ip_whitelist.length === 0 && <div className="text-xs text-[var(--wv-text-3)]">Leave empty to allow any IP.</div>}
              </div>
            </Field>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}
